import { useTranslation } from 'react-i18next'
import { TrendingUp, TrendingDown } from 'lucide-react'
import { Card, CardContent } from '@/components/v2/ui'
import { EID } from '@/lib/element-ids.registry'

interface KpiValue {
  sum: number
  pct: number | null
}

interface PerformanceKpiCardsProps {
  revenue: KpiValue
  quantity: KpiValue
}

interface DeltaBadgeProps {
  pct: number | null
}

function DeltaBadge({ pct }: DeltaBadgeProps) {
  if (pct === null) {
    return <span className="text-sm text-neutral-400 dark:text-neutral-500">—</span>
  }
  if (pct > 0) {
    return (
      <span className="flex items-center gap-1 text-sm font-medium text-emerald-600 dark:text-emerald-400">
        <TrendingUp className="h-4 w-4" aria-hidden="true" />
        {`+${pct.toFixed(0)} %`}
      </span>
    )
  }
  if (pct < 0) {
    return (
      <span className="flex items-center gap-1 text-sm font-medium text-red-600 dark:text-red-400">
        <TrendingDown className="h-4 w-4" aria-hidden="true" />
        {`${pct.toFixed(0)} %`}
      </span>
    )
  }
  return (
    <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
      {`0 %`}
    </span>
  )
}

export function PerformanceKpiCards({ revenue, quantity }: PerformanceKpiCardsProps) {
  const { t } = useTranslation()

  return (
    <div className="grid grid-cols-2 gap-4">
      <Card id={EID['performance-kpi-revenue']}>
        <CardContent className="p-5">
          <p className="mb-1 text-xs text-neutral-500 dark:text-neutral-400">
            {t('performance.metric.revenue')}
          </p>
          <p className="text-2xl font-semibold text-neutral-900 dark:text-neutral-100">
            {`${revenue.sum.toFixed(2)} €`}
          </p>
          <div className="mt-2">
            <DeltaBadge pct={revenue.pct} />
          </div>
        </CardContent>
      </Card>

      <Card id={EID['performance-kpi-quantity']}>
        <CardContent className="p-5">
          <p className="mb-1 text-xs text-neutral-500 dark:text-neutral-400">
            {t('performance.metric.quantity')}
          </p>
          <p className="text-2xl font-semibold text-neutral-900 dark:text-neutral-100">
            {quantity.sum.toLocaleString('de-DE')}
          </p>
          <div className="mt-2">
            <DeltaBadge pct={quantity.pct} />
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
